import React, { useState, useEffect } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { useSession } from '@supabase/auth-helpers-react';
import { useNavigate } from 'react-router-dom';
import { recordSOSEvent } from '@/services/progressService';
import { getAnonymousPlayedGamesCount } from '@/services/anonymousTracking';
import { games } from '@/data/games';
import HeroSection from '@/components/HeroSection';
import GameSelectionDialog from '@/components/GameSelectionDialog';
import PageFooter from '@/components/PageFooter';
import AuthDialog from '@/components/AuthDialog';

// Max number of games a non-authenticated user can play
const MAX_FREE_GAMES = 2;

const Index = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isAuthDialogOpen, setIsAuthDialogOpen] = useState(false);
  const [playedGamesCount, setPlayedGamesCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const session = useSession();
  const navigate = useNavigate();
  
  // Get the number of games played by non-authenticated user
  useEffect(() => {
    if (!session) {
      const fetchPlayedGamesCount = async () => {
        try {
          const count = await getAnonymousPlayedGamesCount();
          setPlayedGamesCount(count);
        } catch (error) {
          console.error("Failed to get played games count:", error);
        } finally {
          setLoading(false);
        }
      };
      
      fetchPlayedGamesCount();
    } else {
      setLoading(false);
    }
  }, [session]);

  const handleSOS = async () => {
    if (session) {
      // Record SOS event for authenticated users
      try {
        await recordSOSEvent();
      } catch (error) {
        console.error("Failed to record SOS event:", error);
      }
    }
    
    setIsDialogOpen(true);
    toast({
      title: "Stay strong!",
      description: "Choose a game to help overcome your craving.",
    });
  };

  const handleGameSelect = (game) => {
    setIsDialogOpen(false);
    
    // Ensure we're navigating with a valid game slug or ID
    if (game) {
      if (game.slug) {
        console.log(`Navigating to game with slug: ${game.slug}`);
        navigate(`/game/${game.slug}`);
      } else if (game.id) {
        console.log(`Navigating to game with ID: ${game.id}`);
        navigate(`/game/${game.id}`);
      } else {
        console.error("Attempted to navigate to game with invalid ID/slug:", game);
        toast({
          title: "Error",
          description: "Could not load the selected game.",
          variant: "destructive",
        });
      }
    } else {
      console.error("Attempted to navigate with undefined game");
      toast({
        title: "Error",
        description: "Could not load the selected game.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-cream-light p-4 flex flex-col">
      <div className="container mx-auto max-w-4xl flex-grow">
        <HeroSection 
          onSOSClick={handleSOS}
          onAuthClick={() => setIsAuthDialogOpen(true)}
          playedGamesCount={playedGamesCount}
          maxFreeGames={MAX_FREE_GAMES}
        />
        
        {/* Game Selection Dialog */}
        <GameSelectionDialog 
          open={isDialogOpen}
          onOpenChange={setIsDialogOpen}
          games={games}
          onGameSelect={handleGameSelect}
          session={session}
          maxFreeGames={MAX_FREE_GAMES}
          playedGamesCount={playedGamesCount}
        />
        
        {/* Auth Dialog for the main page */}
        <AuthDialog 
          open={isAuthDialogOpen}
          onOpenChange={setIsAuthDialogOpen}
        />
      </div>
      
      <PageFooter />
    </div>
  );
};

export default Index;
